/**
 * Storage type definitions for the centralized storage layer
 *
 * This module provides enum-based keys and type definitions for
 * type-safe localStorage operations throughout the application.
 */

import { UserPreferences, OrganizationPreferences } from './preferences.types';
import { AuthUser } from './auth';
import type {
  ListProfilePicturesPicturesItem,
  ListOrgProfilePicturesPicturesItem,
  ListBoardBackgroundsBackgroundsItem,
} from './generated/processor-types';

/**
 * Enum defining all storage keys used in the application.
 * Using enums prevents typos and enables IDE autocomplete.
 */
export enum StorageKey {
  /** User authentication data (user table only) */
  USER = 'user',
  /** User preferences (theme, mood, sizing) from preferences table */
  USER_PREFERENCES = 'user_preferences',
  /** Organization preferences (theme, sizing) from preferences table */
  ORG_PREFERENCES = 'org_preferences',
  /** Access token for API authentication */
  AUTH_TOKEN = 'auth_token',
  /** Sidebar collapse/expand state */
  SIDEBAR_STATE = 'sidebar_state',
  /** Session metadata (login time, last activity, expiry) */
  SESSION_METADATA = 'session_metadata',
  /** Active organization ID (for superadmin org switching) */
  ACTIVE_ORGANIZATION = 'active_org',
  /** Deep link stored for post-login navigation (unauthenticated user hit a deep link) */
  PENDING_DEEP_LINK = 'pending_deep_link',
  /** Cached profile picture list for instant modal rendering */
  PROFILE_PICTURES = 'profile_pictures',
  /** Cached org profile picture list for instant modal rendering */
  ORG_PROFILE_PICTURES = 'org_profile_pictures',
  /** Cached board backgrounds keyed by board_id */
  BOARD_BACKGROUNDS = 'board_backgrounds',
}

/** All sessionStorage keys. sessionStorage is per-tab and refresh-safe. */
export enum SessionKey {
  LB_TAB = 'lb_tab',
  LB_GROUPS_SELECTED_GROUP_ID = 'lb_groups_selectedGroupId',
  LB_COHORTS_SELECTED_GROUP_ID = 'lb_cohorts_selectedGroupId',
  LB_LEARNING_PROGRESS_SELECTED_USER_ID = 'lb_velocity_selectedUserId',
  LB_LEARNING_PROGRESS_SELECTED_ROLE = 'lb_velocity_selectedRole',
  LB_CONFIG_SELECTED_CONFIG_NAME = 'lb_config_selectedConfigName',
  LB_SCHEDULED_JOBS_EXPANDED_JOB_ID = 'lb_scheduledJobs_expandedJobId',

  // Dashboard
  LB_DASHBOARD_BOARD_ID = 'lb_dashboard_boardId',
  LB_DASHBOARD_SELECTED_WEEK = 'lb_dashboard_selectedWeek',

  // UserManagement
  LB_USER_MANAGEMENT_ROLE_FILTER = 'lb_userManagement_roleFilter',

  // OrgsManagement
  LB_ORGS_SHOW_DELETED = 'lb_orgs_showDeleted',

  // People
  LB_PEOPLE_QUERY = 'lb_people_query',
  LB_PEOPLE_SELECTED_USER_ID = 'lb_people_selectedUserId',

  // ScheduledJobs create-job form
  LB_SCHEDULED_JOBS_SELECTED_WEEK_KEY = 'lb_scheduledJobs_selectedWeekKey',
  LB_SCHEDULED_JOBS_SELECTED_TEMPLATE_IDS = 'lb_scheduledJobs_selectedTemplateIds',
  LB_SCHEDULED_JOBS_CREATE_JOB_TYPE = 'lb_scheduledJobs_createJobType',
  LB_SCHEDULED_JOBS_CREATE_FREQUENCY = 'lb_scheduledJobs_createFrequency',
  LB_SCHEDULED_JOBS_CREATE_DAY = 'lb_scheduledJobs_createDay',
  LB_SCHEDULED_JOBS_CREATE_HOUR = 'lb_scheduledJobs_createHour',
  LB_SCHEDULED_JOBS_CREATE_DESCRIPTION = 'lb_scheduledJobs_createDescription',
  LB_SCHEDULED_JOBS_CREATE_ENABLED = 'lb_scheduledJobs_createEnabled',
}

/**
 * Storage schema mapping keys to their value types.
 * Provides type safety for storage operations.
 */
export interface StorageSchema {
  [StorageKey.USER]: AuthUser;
  [StorageKey.USER_PREFERENCES]: UserPreferences;
  [StorageKey.ORG_PREFERENCES]: OrganizationPreferences;
  [StorageKey.AUTH_TOKEN]: string;
  [StorageKey.SIDEBAR_STATE]: boolean;
  [StorageKey.SESSION_METADATA]: {
    login_time: string;
    last_activity: string;
    expiry: string | null;
  };
  [StorageKey.ACTIVE_ORGANIZATION]: string;
  [StorageKey.PROFILE_PICTURES]: ListProfilePicturesPicturesItem[];
  [StorageKey.ORG_PROFILE_PICTURES]: ListOrgProfilePicturesPicturesItem[];
  [StorageKey.BOARD_BACKGROUNDS]: Record<
    string,
    ListBoardBackgroundsBackgroundsItem[]
  >;
}

/**
 * Union type of all supported storage value types.
 * Supports primitive strings and complex JSON-serializable objects.
 */
export type StorageValue = string | number | boolean | object | null;

/**
 * Generic type helper for storage operations.
 * Ensures type safety when getting and setting values.
 */
export type StorageData<T = StorageValue> = T;
